import React from 'react';
import { AlertCircle } from 'lucide-react';
import CustomSelect from '../../../ui/CustomSelect';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';

interface EmploymentTabErrors {
  employee_id?: string;
  join_date?: string;
  department_id?: string;
}

interface EmploymentTabProps {
  formData: any;
  handleChange: (field: string, value: string) => void;
  handleJoinDateChange?: (value: string) => void;
  handlePositionChange?: (value: string) => void;
  handleCompetencyChange?: (value: number) => void;
  departments: any[];
  positions: any[];
  employees: any[];
  employeeId: string | null;
  errors?: EmploymentTabErrors;
  calculatedSalary?: any;
}

export default function EmploymentTab({
  formData,
  handleChange,
  handleJoinDateChange,
  handlePositionChange,
  handleCompetencyChange,
  departments,
  positions,
  employees,
  employeeId,
  errors = {},
  calculatedSalary,
}: EmploymentTabProps) {
  const departmentOptions = departments.map(dept => ({ value: dept.id, label: dept.name }));
  const positionOptions = positions.map(pos => ({ value: pos.id, label: pos.name }));
  const managerOptions = employees
    .filter(e => e.id !== employeeId)
    .map(emp => ({ value: emp.id, label: emp.full_name }));

  const EMPLOYMENT_STATUS_OPTIONS = [
    { value: 'permanent', label: 'Tetap' },
    { value: 'contract', label: 'Kontrak' },
    { value: 'probation', label: 'Percobaan' },
    { value: 'intern', label: 'Magang' },
  ];

  const STATUS_OPTIONS = [
    { value: 'active', label: 'Aktif' },
    { value: 'inactive', label: 'Tidak Aktif' },
    { value: 'terminated', label: 'Berhenti' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Data Kepegawaian</h3>
        <p className="text-gray-500 text-sm">Informasi jabatan dan unit kerja</p>
      </div>

      {/* Alert */}
      <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-blue-800 text-sm">
            Gaji pokok dihitung otomatis berdasarkan:{' '}
            <strong>Jabatan + Masa Kerja + Level Kompetensi</strong> sesuai kebijakan gaji aktif.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* NIP */}
        <div className="space-y-2">
          <Label>
            NIP <span className="text-red-500">*</span>
          </Label>
          <Input
            value={formData.employee_id || ''}
            onChange={e => handleChange('employee_id', e.target.value)}
            className={`focus:outline-none focus:border-emerald-500 transition-colors duration-200 ${
              errors.employee_id ? 'border-red-500' : ''
            }`}
          />
          {errors.employee_id && <p className="text-sm text-red-600">{errors.employee_id}</p>}
        </div>

        {/* Tanggal Mulai Bergabung */}
        <div className="space-y-2">
          <Label>
            Tanggal Mulai Bergabung <span className="text-red-500">*</span>
          </Label>
          <Input
            type="date"
            value={
              formData.join_date
                ? typeof formData.join_date === 'string' && formData.join_date.includes('T')
                  ? formData.join_date.split('T')[0]
                  : formData.join_date
                : ''
            }
            onChange={e => {
              const value = e.target.value;
              if (handleJoinDateChange) {
                handleJoinDateChange(value);
              } else {
                handleChange('join_date', value);
              }
            }}
            className={`focus:outline-none focus:border-emerald-500 transition-colors duration-200 ${
              errors.join_date ? 'border-red-500' : ''
            }`}
          />
          <p className="text-xs text-gray-500">Diperlukan untuk perhitungan masa kerja</p>
          {errors.join_date && <p className="text-sm text-red-600">{errors.join_date}</p>}
        </div>

        {/* Tanggal Awal Kontrak */}
        <div className="space-y-2">
          <Label>Tanggal Awal Kontrak</Label>
          <Input
            type="date"
            value={
              formData.contract_start_date
                ? typeof formData.contract_start_date === 'string' &&
                  formData.contract_start_date.includes('T')
                  ? formData.contract_start_date.split('T')[0]
                  : formData.contract_start_date
                : ''
            }
            onChange={e => handleChange('contract_start_date', e.target.value)}
            className="focus:outline-none focus:border-emerald-500 transition-colors duration-200"
          />
        </div>

        {/* Divisi */}
        <div className="space-y-2">
          <Label>
            Divisi <span className="text-red-500">*</span>
          </Label>
          <CustomSelect
            value={formData.department_id}
            onChange={v => handleChange('department_id', v)}
            placeholder="Pilih divisi"
            width="w-full"
            options={departmentOptions}
            error={!!errors.department_id}
          />
          {errors.department_id && <p className="text-sm text-red-600">{errors.department_id}</p>}
          <p className="text-xs text-blue-600">
            ⚠️ Hak akses ditentukan dari divisi. User harus LOGOUT dan LOGIN lagi setelah pegawai
            dibuat untuk mendapatkan hak akses.
          </p>
        </div>

        {/* Jabatan */}
        <div className="space-y-2">
          <Label>Jabatan</Label>
          <CustomSelect
            value={formData.position_id}
            onChange={v => {
              if (handlePositionChange) {
                handlePositionChange(v);
              } else {
                handleChange('position_id', v);
              }
            }}
            placeholder="Pilih jabatan"
            width="w-full"
            options={positionOptions}
          />
          {formData.position_id && (
            <p className="text-xs text-blue-600">✓ Gaji otomatis dihitung dari kebijakan gaji</p>
          )}
        </div>

        {/* Atasan Langsung */}
        <div className="space-y-2">
          <Label>Atasan Langsung</Label>
          <CustomSelect
            value={formData.manager_id}
            onChange={v => handleChange('manager_id', v)}
            placeholder="Pilih atasan"
            width="w-full"
            options={managerOptions}
          />
        </div>

        {/* Lokasi Kerja */}
        <div className="space-y-2">
          <Label>Lokasi Kerja</Label>
          <Input
            value={formData.work_location || ''}
            onChange={e => handleChange('work_location', e.target.value)}
            className="focus:outline-none focus:border-emerald-500 transition-colors duration-200"
          />
        </div>

        {/* Status Kepegawaian */}
        <div className="space-y-2">
          <Label>Status Kepegawaian</Label>
          <CustomSelect
            value={formData.employment_status}
            onChange={v => handleChange('employment_status', v)}
            placeholder="Pilih status"
            width="w-full"
            options={EMPLOYMENT_STATUS_OPTIONS}
          />
        </div>

        {/* Tanggal Berakhir Kontrak */}
        <div className="space-y-2">
          <Label>Tanggal Berakhir Kontrak</Label>
          <Input
            type="date"
            value={
              formData.end_date
                ? typeof formData.end_date === 'string' && formData.end_date.includes('T')
                  ? formData.end_date.split('T')[0]
                  : formData.end_date
                : ''
            }
            onChange={e => handleChange('end_date', e.target.value)}
            className="focus:outline-none focus:border-emerald-500 transition-colors duration-200"
          />
        </div>

        {/* Status */}
        <div className="space-y-2">
          <Label>Status</Label>
          <CustomSelect
            value={formData.status}
            onChange={v => handleChange('status', v)}
            placeholder="Pilih status"
            width="w-full"
            options={STATUS_OPTIONS}
          />
        </div>
      </div>

      {/* Level Kompetensi & Gaji Pokok */}
      <div className="grid md:grid-cols-3 gap-4 pt-4 border-t">
        {/* Level Kompetensi */}
        <div className="space-y-2">
          <Label>Level Kompetensi *</Label>
          <CustomSelect
            value={formData.competency_level?.toString() || '3'}
            onChange={v => {
              if (handleCompetencyChange) {
                handleCompetencyChange(parseInt(v));
              } else {
                handleChange('competency_level', v);
              }
            }}
            placeholder="Pilih level"
            width="w-full"
            options={[
              { value: '1', label: '1 - Basic' },
              { value: '2', label: '2 - Developing' },
              { value: '3', label: '3 - Competent' },
              { value: '4', label: '4 - Proficient' },
              { value: '5', label: '5 - Expert' },
            ]}
          />
          <p className="text-xs text-gray-500">Hasil assessment kompetensi</p>
        </div>

        {/* Gaji Pokok (Auto-calculated) */}
        <div className="space-y-2 md:col-span-2">
          <Label>Gaji Pokok (Auto-calculated)</Label>
          <Input
            type="text"
            value={
              formData.base_salary
                ? `Rp ${new Intl.NumberFormat('id-ID').format(parseFloat(formData.base_salary))}`
                : '-'
            }
            disabled
            className="bg-gray-100 font-semibold text-emerald-700"
          />
          {calculatedSalary && (
            <div className="text-xs text-gray-700 space-y-1.5 bg-blue-50 border border-blue-200 p-3 rounded-lg mt-2">
              <div className="flex items-center gap-2 pb-1 border-b border-blue-300">
                <span className="font-semibold text-blue-900">
                  📊 {calculatedSalary.policyName}
                </span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Mid Salary (Jabatan):</span>
                  <span className="font-semibold text-gray-900">
                    Rp {new Intl.NumberFormat('id-ID').format(calculatedSalary.midSalary)}
                  </span>
                </div>
                {calculatedSalary.tenureAdjustment !== 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Masa Kerja:</span>
                    <span className="font-semibold text-emerald-600">
                      +Rp {new Intl.NumberFormat('id-ID').format(calculatedSalary.tenureAdjustment)}
                    </span>
                  </div>
                )}
                {calculatedSalary.competencyAdjustment !== 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Kompetensi:</span>
                    <span className="font-semibold text-purple-600">
                      +Rp{' '}
                      {new Intl.NumberFormat('id-ID').format(calculatedSalary.competencyAdjustment)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-1 border-t border-blue-300 mt-1">
                  <span className="font-semibold text-blue-900">Total:</span>
                  <span className="font-bold text-lg text-blue-900">
                    Rp {new Intl.NumberFormat('id-ID').format(calculatedSalary.total)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Data Keuangan */}
        <div className="space-y-2">
          <Label>Nama Bank</Label>
          <Input
            value={formData.bank_name || ''}
            onChange={e => handleChange('bank_name', e.target.value)}
            className="focus:outline-none focus:border-emerald-500 transition-colors duration-200"
          />
        </div>

        <div className="space-y-2">
          <Label>No. Rekening</Label>
          <Input
            value={formData.bank_account || ''}
            onChange={e => handleChange('bank_account', e.target.value)}
            className="focus:outline-none focus:border-emerald-500 transition-colors duration-200"
          />
        </div>

        <div className="space-y-2">
          <Label>NPWP</Label>
          <Input
            value={formData.npwp || ''}
            onChange={e => handleChange('npwp', e.target.value)}
            className="focus:outline-none focus:border-emerald-500 transition-colors duration-200"
          />
        </div>

        <div className="space-y-2">
          <Label>BPJS Kesehatan</Label>
          <Input
            value={formData.bpjs_kesehatan || ''}
            onChange={e => handleChange('bpjs_kesehatan', e.target.value)}
            className="focus:outline-none focus:border-emerald-500 transition-colors duration-200"
          />
        </div>

        <div className="space-y-2">
          <Label>BPJS Ketenagakerjaan</Label>
          <Input
            value={formData.bpjs_ketenagakerjaan || ''}
            onChange={e => handleChange('bpjs_ketenagakerjaan', e.target.value)}
            className="focus:outline-none focus:border-emerald-500 transition-colors duration-200"
          />
        </div>
      </div>
    </div>
  );
}
